import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Ban, MessageCircleQuestion, Pause, Play, RotateCcw, Send, Workflow } from "lucide-react";
import { useCase, useCaseEvents, useCaseSteps } from "@/api/queries/cases";
import {
  useAbortCase,
  useAnswerCase,
  usePauseCase,
  useRedispatchCase,
  useResumeCase,
  useRetryCase,
  useSetCaseModel,
} from "@/api/mutations/cases";
import { useAgentModels } from "@/api/queries/models";
import { fixLabelAus, laufModellOptionen, mitAltwert, modelBasisAus } from "@/lib/modelTarget";
import { NATIVE_SELECT_CLASS } from "@/lib/formClasses";
import { useProcess } from "@/api/queries/processes";
import { useProcessInstance } from "@/api/queries/processInstances";
import { useUsers } from "@/api/queries/users";
import { ApiError } from "@/api/client";
import { BackLink, DetailHeader } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EntityLink } from "@/components/EntityLink";
import { CaseStatusBadge } from "@/components/ProcessBits";
import { ApprovalCard } from "@/components/case-timeline/ApprovalCard";
import { CaseFilesCard } from "@/components/case-timeline/CaseFilesCard";
import { StepTimeline, alsSchrittListe, stepAnkerId } from "@/components/case-timeline/StepTimeline";
import { ProcessGraphViewer } from "@/components/process-graph/ProcessGraphViewer";
import { Textarea } from "@/components/ui/textarea";
import { relativeTime } from "@/lib/relativeTime";
import { formatUsd } from "@/lib/formatCost";
import type { CaseEventView, CaseView, PendingAsk } from "@/types";

// #301 (UC-5b): ein Lauf im Detail — Fortschritt, Ergebnis, Verlauf, Lebenslauf.
//
// Der Verlauf ist zugleich die Auskunftssicht (Konzept §6.1): wer war beteiligt, was wurde
// entschieden, welche Felder wurden gesetzt.

const SCHRITT_LABEL: Record<string, string> = {
  pending: "offen",
  running: "läuft",
  waiting: "wartet",
  blocked: "blockiert",
  done: "erledigt",
  skipped: "übersprungen",
};

const SCHRITT_PUNKT: Record<string, string> = {
  done: "bg-ok",
  running: "bg-accent",
  waiting: "bg-warn",
  blocked: "bg-danger",
  skipped: "bg-muted",
  pending: "bg-border-strong",
};

// Die Ereignis-Arten in Klartext. Ein Verlauf, der „step_done" sagt, ist ein Log —
// einer, der „Schritt abgeschlossen" sagt, ist eine Auskunft.
const EREIGNIS_LABEL: Record<string, string> = {
  start: "Lauf gestartet",
  dispatch: "Schritt an den Agenten übergeben",
  step_done: "Schritt abgeschlossen",
  step_blocked: "Schritt blockiert",
  step_skipped: "Schritt übersprungen",
  ask: "Rückfrage gestellt",
  answer: "Rückfrage beantwortet",
  expired: "Frist abgelaufen",
  nudge: "Erinnerung geschickt",
  abort: "Lauf abgebrochen",
  pause: "Lauf pausiert",
  resume: "Lauf fortgesetzt",
  escalation: "eskaliert",
  // #P001 (UC-5): der Laufzeit-Wechsel. Die Namen stehen im `data` — `ereignisTitel` hängt sie
  // an, damit im Verlauf „Modell gewechselt: sonnet → haiku" steht und nicht nur die Gattung.
  model_changed: "Modell gewechselt",
  error: "Fehler",
  done: "Lauf abgeschlossen",
};

/**
 * #P001 (UC-5): die Überschrift eines Ereignisses. Für den Modell-Wechsel trägt sie die beiden
 * Namen mit — sie sind die ganze Aussage, und im aufgeklappten JSON darunter zu suchen wäre für
 * genau die Frage („womit lief der Schritt, der danach brach?") der falsche Weg.
 *
 * Version-Skew-fest: fehlen die Felder (alter Master, gekürztes Ereignis), bleibt es beim Label.
 */
function ereignisTitel(e: CaseEventView): string {
  const label = EREIGNIS_LABEL[e.type] ?? e.type;
  if (e.type !== "model_changed") return label;
  const from = typeof e.data.from === "string" ? e.data.from : null;
  const to = typeof e.data.to === "string" ? e.data.to : null;
  return from && to ? `${label}: ${from} → ${to}` : label;
}

function Ereignis({ e }: { e: CaseEventView }) {
  const hatDaten = Object.keys(e.data).length > 0;
  return (
    <div className="flex items-start gap-3 py-2" data-testid="case-event">
      <span className="w-[130px] shrink-0 pt-0.5 font-mono text-[11.5px] text-text3">
        {new Date(e.ts).toLocaleString("de-CH")}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold text-text">
          {ereignisTitel(e)}
          {e.stepId && <span className="ml-1.5 font-mono text-[11.5px] font-normal text-text2">· {e.stepId}</span>}
          {e.attempt !== null && e.attempt > 1 && (
            <span className="ml-1.5 text-[11.5px] font-normal text-warn">Versuch {e.attempt}</span>
          )}
        </div>
        <div className="text-[11.5px] text-text3">{e.actor}</div>
        {/* `whitespace-pre-wrap` ist kein Kosmetik-Detail: die wichtigste Zeile eines
            blockierten Laufs ist der GRUND, und der ist ein langer Fliesstext. Ohne Umbruch
            lief er rechts aus dem Kasten — sichtbar am ersten Live-Durchgang. */}
        {hatDaten && (
          <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-md bg-surface-2 p-2 font-mono text-[11px] text-text2">
            {JSON.stringify(e.data, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

/**
 * #301 (UC-5d): die offene Rückfrage — ganz oben, als AUFGABE.
 *
 * Vorher stand sie an genau zwei Stellen: als Fließtext in einer Benachrichtigung und im
 * Verlauf als `JSON.stringify` einer Ereignis-Nutzlast, also zwischen geschweiften Klammern
 * und vierzehn anderen Zeilen. Ein Antwort-Feld gab es nirgends, obwohl die Route seit UC-5a
 * existiert. Im Verlauf bleibt das Ereignis stehen — dort ist es Historie, hier ist es
 * Aufgabe.
 */
/**
 * „wartet seit 3min" — dieselbe Staffelung wie `relativeTime`, nur in der Warte-Form.
 * Ein unbrauchbarer Zeitpunkt wird BENANNT statt als „gerade eben" behauptet.
 */
function wartetSeit(iso: string | null): string {
  if (iso === null) return "Zeitpunkt unbekannt";
  const rel = relativeTime(iso);
  if (rel.startsWith("vor ")) return `wartet seit ${rel.slice(4)}`;
  if (rel === "gerade eben") return "gerade eingegangen";
  return "Zeitpunkt unbekannt";
}

function RueckfrageKarte({
  ask,
  caseId,
  onAntwort,
  isPending,
}: {
  ask: PendingAsk;
  caseId: string;
  onAntwort: (id: string, antwort: string) => void;
  isPending: boolean;
}) {
  const [text, setText] = useState("");
  return (
    <SectionCard
      title="Rückfrage — der Lauf wartet auf Ihre Antwort"
      description={`Schritt „${ask.stepName}“ · ${wartetSeit(ask.gestelltAm)}`}
      icon={<MessageCircleQuestion className="h-4 w-4 text-warn" />}
      className="border-warn-border"
      testId="case-pending-ask"
    >
      {/* Ist die Frage nicht abrufbar, wird das BENANNT — und das Antwortfeld bleibt: es
          ist der einzige Weg, den wartenden Lauf wieder in Gang zu bringen (sonst bliebe
          nur Abbrechen). Die Antwort-Route hängt nicht am Fragetext. */}
      {ask.frage ? (
        <p className="whitespace-pre-wrap break-words text-[13px] text-text" data-testid="pending-ask-frage">
          {ask.frage}
        </p>
      ) : (
        <p className="text-[13px] text-warn" data-testid="pending-ask-frage-fehlt">
          Frage nicht abrufbar — der Lauf wartet trotzdem auf eine Antwort.
        </p>
      )}
      <Textarea
        className="mt-3"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Ihre Antwort — sie geht als Anweisung in den nächsten Schritt."
        data-testid="pending-ask-input"
      />
      <div className="mt-3 flex justify-end">
        <Button
          // Leerer Text ist keine Antwort — der Master lehnt ihn ohnehin ab, aber ein
          // gesperrter Knopf sagt das, bevor jemand dagegen läuft.
          disabled={text.trim() === "" || isPending}
          onClick={() => onAntwort(caseId, text)}
          data-testid="pending-ask-submit"
        >
          Antwort absenden
        </Button>
      </div>
    </SectionCard>
  );
}

/**
 * Die Einrichtung, aus der der Lauf entstanden ist — oder die ehrliche Auskunft, dass es sie
 * nicht mehr gibt. Seit UC-5b überleben Läufe ihre Instanz; ein Link ins Leere wäre die
 * schlechtere Antwort als „gelöscht".
 *
 * VIER unterscheidbare Zustände, mit Absicht: nur ein 404 heißt „weg". Jeder andere Fehler
 * (Netz, 5xx) sagt „nicht abrufbar" — sonst behauptete eine kurze Störung eine Löschung, die
 * nie stattgefunden hat. Und „lädt noch" darf nicht aussehen wie „Fehler", sonst prüft auch
 * kein Test die Unterscheidung (genau daran ist die erste Fassung vorbeigelaufen).
 */
function InstanzZeile({ caseData }: { caseData: CaseView }) {
  const instance = useProcessInstance(caseData.instanceId);
  if (instance.isError) {
    const weg = instance.error instanceof ApiError && instance.error.status === 404;
    return weg ? (
      <span className="text-[12.5px] text-text3" data-testid="instance-deleted">
        Einrichtung gelöscht — der Lauf bleibt erhalten
      </span>
    ) : (
      <span className="text-[12.5px] text-text2" data-testid="instance-unknown">
        <span className="font-mono text-[12px]">{caseData.instanceId}</span>
        <span className="ml-1.5 text-text3">(nicht abrufbar)</span>
      </span>
    );
  }
  if (instance.data) {
    return (
      <Link
        to={`/process-instances/${caseData.instanceId}`}
        className="text-[12.5px] text-accent hover:underline"
        data-testid="instance-link"
      >
        Instanz öffnen
      </Link>
    );
  }
  return (
    <span className="font-mono text-[12px] text-text2" data-testid="instance-loading">
      {caseData.instanceId}
    </span>
  );
}

export function CaseDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: c, isLoading, isError } = useCase(id);
  const events = useCaseEvents(id);
  // #303 (UC-2): die Schritt-Faltung — dieselbe Chronik, nach Schritten sortiert.
  const schritte = useCaseSteps(id);
  const template = useProcess(c?.templateId);
  // #303 (Review H1/M2): die Feld-Beschreibungen der Freigabe UND die Kosten kommen seit
  // der Review-Runde mit dem Lauf-Detail selbst. Vorher holte diese Seite beides über
  // eigene Abfragen — die Konfigurations-Sicht der Einrichtung (für den Entscheider ein
  // 404, er ist nicht ihr Inhaber) und die agent-gebundene Sitzungs-Route (zwei
  // zusätzliche Anfragen je Aufruf, für Nicht-Admins ebenfalls ein 404).
  const users = useUsers();
  const abort = useAbortCase();
  const pause = usePauseCase();
  const resume = useResumeCase();
  const retry = useRetryCase();
  const redispatch = useRedispatchCase();
  const answer = useAnswerCase();
  const setModel = useSetCaseModel();
  // #P001 (UC-5): die wählbaren Modelle DIESES Agenten. Vor den frühen Rückgaben gerufen
  // (Hook-Regel) — ohne `agentId` (Beteiligten-Sicht, noch ladend) wird nichts geholt.
  const modelle = useAgentModels(c?.agentId);
  const [abortOpen, setAbortOpen] = useState(false);

  // Klick im Ablauf-Bild → zur Karte des Schritts. `scroll-mt` an der Karte hält sie
  // unter der klebenden Kopfzeile.
  const springeZuSchritt = (stepId: string) => {
    document.getElementById(stepAnkerId(stepId))?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // EINE gehärtete Schritt-Liste für Bild, Zeitleiste und Datei-Karte — statt drei Stellen,
  // die je für sich auf ein Server-Feld hoffen. Gefaltet wird EINMAL je Antwort (Review L4):
  // vorher taten es Seite und Zeitleiste getrennt, also zweimal pro Live-Takt.
  const gefalteteSchritte = useMemo(() => alsSchrittListe(schritte.data), [schritte.data]);

  if (isLoading && !c) {
    return (
      <div className="space-y-4">
        <BackLink to="/cases" label="Läufe" />
        <p className="text-[13px] text-text3">Lade…</p>
      </div>
    );
  }
  if (isError || !c) {
    return (
      <div className="space-y-4">
        <BackLink to="/cases" label="Läufe" />
        <p className="text-[13px] text-danger" data-testid="detail-error">
          Lauf nicht gefunden.
        </p>
      </div>
    );
  }

  const terminal = c.status === "done" || c.status === "aborted";
  // #P001 (UC-5): der Wechsel-Picker. `targets` beschreibt GENAU die wählbaren Namen; fehlt es
  // (älterer Master), bleibt die blosse Namensliste — lieber kein Ziel als ein erfundenes.
  const modelDaten = modelle.data;
  // Geteilte Ableitung (Review UC-5 MED-3) — eine Regel für alle drei Picker.
  const modelBasis = modelBasisAus(modelDaten);
  const modelFix = fixLabelAus(modelDaten);
  // Der laufende Wert bleibt sichtbar, auch wenn er nicht mehr zur Wahl steht — still
  // verschwinden hiesse, die Liste behauptet einen Wechsel, den niemand ausgelöst hat.
  // `belastbar` nur mit GELADENER Liste: bei Ladefehler wäre „(nicht mehr wählbar)" erfunden.
  // Unter einem Admin-Fix ist die Liste leer und `c.model` IST der Fix (wirksames Modell, UC-4) —
  // ein „(nicht mehr wählbar)" daneben widerspräche dem Tooltip (Review MED-2). Marker nur ohne Fix.
  // #P002 (UC-5): der Master liefert die bereits gefilterte Menge (`modelChoices`); fehlt sie
  // (älterer Master, Beteiligten-Sicht) oder ist sie `null`, bleibt die ungefilterte Basis.
  const { optionen: laufOptionen, gefiltert: modellGefiltert } = laufModellOptionen(c.modelChoices, modelBasis);
  const modelOptionen = mitAltwert(laufOptionen, c.model, modelFix === null && !!modelDaten);
  // #302 (UC-5): fehlt in der Beteiligten-Sicht — die Sektion bleibt dann schlicht leer.
  const casefields = Object.entries(c.casefields ?? {});
  const ownerEmail = c.ownerId ? users.data?.find((u) => u.id === c.ownerId)?.email : undefined;

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo="/cases"
        backLabel="Läufe"
        icon={Workflow}
        title={template.data?.name ?? c.templateId}
        badge={<CaseStatusBadge status={c.status} />}
        sub={c.id}
        actions={
          <div className="flex items-center gap-2">
            {/* #P001 (UC-5): das Modell für die noch kommenden Schritte. Nur solange noch einer
                kommt, und nur mit sichtbarem Agenten (die Beteiligten-Sicht hat keinen — und
                ohne ihn gibt es die Frage „welche Modelle?" gar nicht). */}
            {!terminal && c.agentId && (
              <select
                value={c.model ?? ""}
                onChange={(e) => {
                  const gewaehlt = e.target.value;
                  if (!gewaehlt || gewaehlt === c.model) return;
                  const label = modelOptionen.find((o) => o.value === gewaehlt)?.label ?? gewaehlt;
                  setModel.mutate(
                    { id: c.id, model: gewaehlt },
                    {
                      onSuccess: (antwort) => {
                        toast.success(`Modell ab dem nächsten Schritt: ${label}`);
                        // Der Hinweis ist die Entscheidung aus §16: erlaubt, aber ungetestet —
                        // deshalb lange sichtbar und mit dem Weg zurück im Text.
                        if (antwort.warning === "provider-change") {
                          toast.warning(
                            "Wechsel über Anbietergrenzen ist ungetestet — der Lauf könnte am nächsten Schritt mit einem Transkript-Fehler blockieren; dann Modell zurückstellen und „Erneut versuchen“.",
                            { duration: 10_000 },
                          );
                        }
                      },
                      onError: (err) =>
                        toast.error(
                          // ⚠ #P002 (UC-5, Review 8 LOW-2): dasselbe 400 hat seit UC-5 ZWEI
                          // Ursachen — nicht (mehr) wählbar ODER erfüllt die Anforderungen der
                          // Vorlage nicht. Der Text nennt beide, sonst sucht der Inhaber an der
                          // Wählbarkeit, während die Vorlage der Grund ist.
                          err instanceof ApiError && err.status === 400
                            ? "Dieses Modell ist für diesen Lauf nicht zugelassen — entweder nicht (mehr) wählbar oder es erfüllt die Anforderungen der Vorlage nicht. Liste neu laden und aus den angebotenen wählen."
                            : err instanceof ApiError && err.status === 409
                              ? "Das Modell ist in diesem Zustand nicht änderbar"
                              : "Modell konnte nicht gewechselt werden",
                        ),
                    },
                  );
                }}
                disabled={setModel.isPending || modelle.isLoading || modelFix !== null}
                title={
                  modelFix !== null
                    ? `Admin-Fix: ${modelFix}`
                    : modellGefiltert
                      ? "Modell für die nächsten Schritte — nur Modelle, die die Anforderungen aller Schritte erfüllen"
                      : "Modell für die nächsten Schritte"
                }
                className={`${NATIVE_SELECT_CLASS} h-9 w-56`}
                data-testid="case-model"
                /* #P002 (UC-5): dass die Liste durch die Anforderungen der Schritte kürzer ist,
                   sagt der `title` (siehe oben) — der Picker steht in der Kopf-Aktionsleiste,
                   für eine eigene Hinweiszeile ist dort kein Platz. Das Attribut macht es
                   zusätzlich prüfbar. */
                data-gefiltert={modellGefiltert ? "true" : undefined}
              >
                {/* Kein leerer „Standard"-Eintrag: der Lauf HAT ein Modell, und „zurück zur
                    Kette" gibt es zur Laufzeit nicht — die Session trägt einen Namen. */}
                {c.model === null || c.model === undefined ? <option value="">—</option> : null}
                {modelOptionen.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
            {!terminal &&
              (c.status === "paused" ? (
                <Button
                  variant="outline"
                  onClick={() =>
                    resume.mutate(c.id, {
                      onSuccess: () => toast.success("Fortgesetzt"),
                      onError: () => toast.error("Fortsetzen fehlgeschlagen"),
                    })
                  }
                  disabled={resume.isPending}
                  data-testid="case-resume"
                >
                  <Play className="h-4 w-4" />
                  Fortsetzen
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() =>
                    pause.mutate(c.id, {
                      onSuccess: () => toast.success("Pausiert"),
                      onError: () => toast.error("Pausieren fehlgeschlagen"),
                    })
                  }
                  disabled={pause.isPending}
                  data-testid="case-pause"
                >
                  <Pause className="h-4 w-4" />
                  Pausieren
                </Button>
              ))}
            {/* #301 (UC-5c): der Gegenpart zu „Abbrechen". Nur bei `blocked` — in jedem
                anderen Zustand gibt es nichts zu wiederholen. */}
            {c.status === "blocked" && (
              <Button
                variant="outline"
                onClick={() =>
                  retry.mutate(c.id, {
                    onSuccess: (aktualisiert) => toast.success(`Erneut versucht — Lauf ist ${aktualisiert.status}`),
                    onError: () => toast.error("Erneut versuchen fehlgeschlagen"),
                  })
                }
                disabled={retry.isPending}
                data-testid="case-retry"
              >
                <RotateCcw className="h-4 w-4" />
                Erneut versuchen
              </Button>
            )}
            {/* #302 (UC-3) / #303 (UC-2): dieselbe WIRKUNG wie „Erneut versuchen", aber ein
                anderer Kreis darf sie auslösen — auch die entscheidende Rolle des offenen
                Schritts, nicht nur der Inhaber. Wer die Blockade fachlich gelöst hat
                („im Zielsystem geprüft"), schliesst den Vorgang damit selbst ab, statt den
                Inhaber um einen Klick zu bitten, den er nicht beurteilen kann. */}
            {c.status === "blocked" && (
              <Button
                variant="outline"
                onClick={() =>
                  redispatch.mutate(c.id, {
                    onSuccess: () => toast.success("Erneut angestossen"),
                    onError: (err) =>
                      toast.error(
                        err instanceof ApiError && err.status === 403
                          ? "Sie tragen die entscheidende Rolle für diesen Schritt nicht"
                          : "Erneut anstossen fehlgeschlagen",
                      ),
                  })
                }
                disabled={redispatch.isPending}
                data-testid="case-redispatch"
              >
                <Send className="h-4 w-4" />
                Erneut anstossen
              </Button>
            )}
            {!terminal && (
              <Button variant="ghost" onClick={() => setAbortOpen(true)} data-testid="case-abort">
                <Ban className="h-4 w-4" />
                Abbrechen
              </Button>
            )}
          </div>
        }
      />

      {/* Vor den Eigenschaften: was auf eine Entscheidung wartet, gehört nach oben. */}
      {c.pendingAsk && (
        <RueckfrageKarte
          // Key an die Frage: eine neue Rückfrage bekommt ein frisches Eingabefeld, statt
          // den Entwurf zur vorigen weiterzuschleppen.
          key={c.pendingAsk.gestelltAm ?? c.pendingAsk.stepId}
          ask={c.pendingAsk}
          caseId={c.id}
          isPending={answer.isPending}
          onAntwort={(id, antwort) =>
            answer.mutate(
              { id, answer: antwort },
              {
                onSuccess: () => toast.success("Antwort abgeschickt"),
                // Der Text bleibt im Feld stehen (kein Reset im Fehlerfall) — er ist
                // womöglich lang, und ihn wegzuwerfen wäre die teuerste Reaktion.
                onError: () => toast.error("Antwort konnte nicht abgeschickt werden"),
              },
            )
          }
        />
      )}

      {/* #302 (UC-1) / #303 (UC-2): die offene Freigabe. Bis hierher stand ein Lauf auf
          „wartet auf Freigabe" und die Oberfläche bot nichts an — der einzige Ausgang war
          Abbrechen. */}
      {c.pendingApproval && (
        <ApprovalCard
          key={c.pendingApproval.angefragtAm ?? c.pendingApproval.stepId}
          caseId={c.id}
          approval={c.pendingApproval}
        />
      )}

      {/* #303 (UC-3): der Ablauf als Bild, mit dem Stand des Laufs darüber gelegt.
          Steht UNTER den Aufgaben-Karten und über allem anderen: Orientierung kommt vor
          Detail, aber eine wartende Entscheidung kommt vor Orientierung. */}
      {gefalteteSchritte.length > 0 && (
        <ProcessGraphViewer
          steps={gefalteteSchritte}
          currentStepId={c.currentStepId}
          onStepClick={springeZuSchritt}
        />
      )}

      <PropGrid
        rows={[
          [
            "Prozess",
            <Link key="t" to={`/processes/${c.templateId}`} className="text-[12.5px] text-accent hover:underline">
              {template.data?.name ?? c.templateId}
            </Link>,
          ],
          ["Version", <span key="v" className="font-mono text-[12.5px]">{c.templateVersion}</span>],
          ["Einrichtung", <InstanzZeile key="i" caseData={c} />],
          [
            "Agent",
            // #302 (UC-5): in der Beteiligten-Sicht FEHLT der Agent (Id und Klarname).
            // Ohne diesen Guard rendert React "/agents/undefined" als gültig aussehenden
            // Link — das Anzeige-Muster der Datei für "gibt es hier nicht" ist "—".
            c.agentId ? (
              <Link key="a" to={`/agents/${c.agentId}`} className="font-mono text-[12.5px] text-accent hover:underline">
                {c.agentName ?? c.agentId}
              </Link>
            ) : (
              <span key="a" className="text-[12.5px] text-text3">
                —
              </span>
            ),
          ],
          [
            "Inhaber",
            c.ownerId ? (
              <EntityLink
                key="o"
                to={`/users/${c.ownerId}`}
                id={c.ownerId}
                label={ownerEmail}
                isResolving={users.isLoading || users.isError}
              />
            ) : (
              // Läufe von vor Migration 048 tragen keinen eingefrorenen Inhaber.
              <span key="o" className="text-[12.5px] text-text3">
                nicht vermerkt (Lauf vor der Umstellung)
              </span>
            ),
          ],
          ["Auslöser", c.triggerKind + (c.startedBy ? ` · ${c.startedBy}` : "")],
          ...(c.dryRun ? [["Modus", "Probelauf (keine Außenwirkung)"] as [string, string]] : []),
          [
            "Kosten",
            // `undefined` (Beteiligten-Sicht / älterer Master) und `null` (nicht
            // nachschlagbar) sind beide „—", nie „$0.00": „nicht abrufbar" und „hat nichts
            // gekostet" sind zwei Aussagen.
            typeof c.costUsd === "number" ? (
              <span key="k" className="text-[12.5px]">{formatUsd(c.costUsd)}</span>
            ) : (
              <span key="k" className="text-[12.5px] text-text3">—</span>
            ),
          ],
          [
            "Modell",
            // #P001 (UC-4): womit der Lauf arbeitet. `undefined` (Beteiligten-Sicht / älterer
            // Master) und `null` (nicht nachschlagbar) sind beide „—" — womit er GESTARTET
            // ist, steht im Verlauf am `start`-Ereignis.
            c.model ? (
              <span key="m" className="font-mono text-[12px]">{c.model}</span>
            ) : (
              <span key="m" className="text-[12.5px] text-text3">—</span>
            ),
          ],
          ["Gestartet", new Date(c.createdAt).toLocaleString("de-CH")],
          ["Beendet", c.finishedAt ? new Date(c.finishedAt).toLocaleString("de-CH") : "—"],
          // #328 (UC-4): stille Zeile. „—" heisst „wird nicht gelöscht" — kein Regler im
          // Prozess deklariert (dann bleibt alles) oder der Lauf ist noch nicht beendet.
          ["Löschung", c.loeschDatum ? new Date(c.loeschDatum).toLocaleDateString("de-CH") : "—"],
        ]}
      />

      {/* #303 (UC-2): die Schritt-Sicht — „ich will in jedem Step sehen, was Input/Output
          war" (Arben, 2026-08-27). Der Verlauf weiter unten bleibt die Chronik; hier steht
          dieselbe Wahrheit pro Schritt gefaltet.

          Fällt die Faltung aus, tritt die alte Schritt-Kette an ihre Stelle — sie kommt aus
          dem Lauf-Detail selbst und ist damit immer da. Eine leere Sektion wäre die
          schlechtere Antwort: sie sähe aus wie „dieser Lauf hat keine Schritte". */}
      <SectionCard title="Schritte">
        {schritte.data ? (
          <StepTimeline caseId={c.id} daten={schritte.data} schritte={gefalteteSchritte} />
        ) : (
          <>
            {schritte.isError && (
              <p className="mb-2 text-[12.5px] text-warn" data-testid="steps-fehler">
                Die Schritt-Sicht ist nicht abrufbar — unten steht nur der Fortschritt.
              </p>
            )}
            <ol className="space-y-1.5">
              {c.steps.map((s) => (
                <li key={s.id} className="flex items-center gap-2.5 text-[12.5px]" data-testid="case-step">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${SCHRITT_PUNKT[s.status] ?? "bg-border-strong"}`} />
                  <span className={`font-mono ${s.id === c.currentStepId ? "font-semibold text-text" : "text-text2"}`}>
                    {s.id}
                  </span>
                  <span className="text-text3">{SCHRITT_LABEL[s.status] ?? s.status}</span>
                  {s.id === c.currentStepId && c.attempt > 1 && (
                    <span className="text-[11.5px] text-warn">Versuch {c.attempt}</span>
                  )}
                </li>
              ))}
            </ol>
          </>
        )}
      </SectionCard>

      {/* #303 (UC-2, Bau-Nachtrag): die Abgaben des Laufs mit Versions-Faltung.
          Blendet sich aus, solange es keine gibt. */}
      <CaseFilesCard caseId={c.id} steps={gefalteteSchritte} />

      {casefields.length > 0 && (
        <SectionCard title="Vorgangsdaten">
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {casefields.map(([k, v]) => (
              <div key={k} className="min-w-0">
                <dt className="font-mono text-[11.5px] text-text3">{k}</dt>
                <dd className="break-words text-[12.5px] text-text">
                  {typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)}
                </dd>
              </div>
            ))}
          </dl>
        </SectionCard>
      )}

      <SectionCard title="Verlauf">
        {events.isError ? (
          // Ein Ladefehler wird BENANNT — „keine Einträge" wäre eine Falschaussage.
          <p className="text-[12.5px] text-danger">Verlauf nicht abrufbar.</p>
        ) : (events.data ?? []).length === 0 ? (
          <p className="text-[12.5px] text-text3">Noch keine Ereignisse.</p>
        ) : (
          <div className="divide-y divide-border">
            {(events.data ?? []).map((e, i) => (
              <Ereignis key={e.id ?? i} e={e} />
            ))}
          </div>
        )}
      </SectionCard>

      <ConfirmDialog
        open={abortOpen}
        onOpenChange={setAbortOpen}
        title="Lauf abbrechen?"
        description="Der Lauf wird beendet und nicht fortgesetzt. Bereits erfolgte Schritte bleiben im Verlauf sichtbar; was der Agent nach außen getan hat, wird nicht rückgängig gemacht."
        confirmText="Abbrechen"
        confirmVariant="destructive"
        isPending={abort.isPending}
        onConfirm={() =>
          abort.mutate(
            { id: c.id },
            {
              onSuccess: () => {
                toast.success("Lauf abgebrochen");
                setAbortOpen(false);
              },
              onError: () => toast.error("Abbrechen fehlgeschlagen"),
            },
          )
        }
      />
    </div>
  );
}
